import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadCSV } from "@/lib/csv";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, Building2, UserPlus, PoundSterling, Download } from "lucide-react";

export default function Waitlist() {
    const [data, setData] = useState({ count: 0, paid: 0, items: [] });
    useEffect(() => { api.get("/pre-registrations").then((r) => setData(r.data)).catch(() => {}); }, []);

    const exportCsv = () => downloadCSV(`pre-registrations-${new Date().toISOString().slice(0, 10)}.csv`, data.items, [
        { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "organisation", label: "Organisation" },
        { key: "role", label: "Role" }, { key: "interest", label: "Interest" }, { key: "wants", label: "Comment" },
        { key: "status", label: "Status" }, { key: "created_at", label: "Registered" },
    ]);

    return (
        <div className="space-y-6" data-testid="waitlist-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">Pre-registrations</h1>
                    <p className="text-slate-500 mt-1">Founding members who pre-registered for EDI Connect.</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" className="gap-2" onClick={exportCsv} disabled={data.items.length === 0} data-testid="prereg-export-csv"><Download className="h-4 w-4" /> Export CSV</Button>
                    <div className="bg-white rounded-lg border border-slate-200 shadow-sm px-6 py-3 text-center" data-testid="prereg-total">
                        <p className="font-heading text-3xl font-extrabold text-nhs-ink">{data.count}</p>
                        <p className="text-xs text-slate-500">total</p>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-200 shadow-sm px-6 py-3 text-center" data-testid="prereg-paid">
                        <p className="font-heading text-3xl font-extrabold text-nhs-teal">{data.paid}</p>
                        <p className="text-xs text-slate-500">paid</p>
                    </div>
                </div>
            </div>

            {data.items.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-400 flex flex-col items-center gap-3" data-testid="empty-waitlist">
                    <UserPlus className="h-8 w-8" /> No pre-registrations yet.
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">
                    {data.items.map((w) => (
                        <div key={w.id} className="p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`prereg-${w.id}`}>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-nhs-ink">{w.name}</span>
                                    {w.role && <Badge className="bg-nhs-blue/10 text-nhs-blue border-0">{w.role}</Badge>}
                                    {w.status === "paid"
                                        ? <Badge className="bg-nhs-teal/15 text-nhs-teal border-0 gap-1"><PoundSterling className="h-3 w-3" />Paid £365</Badge>
                                        : <Badge className="bg-slate-100 text-slate-500 border-0">Pending payment</Badge>}
                                </div>
                                <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                                    <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-slate-400" /> {w.email}</span>
                                    {w.organisation && <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4 text-slate-400" /> {w.organisation}</span>}
                                </div>
                                {w.wants && <p className="mt-2 text-sm text-slate-700">"{w.wants}"</p>}
                            </div>
                            <span className="text-xs text-slate-400 shrink-0">{new Date(w.created_at).toLocaleDateString()}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
